import * as THREE from 'three';

const COUNT = 900;
const BOX = 1400; // metres around the camera (sized for capital ships)

/**
 * Space dust streaks around the camera. Streak length follows the ship velocity relative to the
 * local frame, so motion reads at any speed (capped, then only the flicker rate rises).
 */
export class SpeedDust {
  readonly object: THREE.LineSegments;
  private readonly base = new Float32Array(COUNT * 3);
  private readonly positions: Float32Array;
  private readonly alphas: Float32Array;
  private readonly offset = new THREE.Vector3();

  constructor() {
    const geo = new THREE.BufferGeometry();
    this.positions = new Float32Array(COUNT * 6);
    this.alphas = new Float32Array(COUNT * 2);
    for (let i = 0; i < COUNT; i++) {
      this.base[i * 3] = Math.random() * BOX;
      this.base[i * 3 + 1] = Math.random() * BOX;
      this.base[i * 3 + 2] = Math.random() * BOX;
    }
    geo.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    geo.setAttribute('alpha', new THREE.BufferAttribute(this.alphas, 1));
    const mat = new THREE.ShaderMaterial({
      uniforms: { opacity: { value: 0 } },
      vertexShader: /* glsl */ `
        #include <common>
        #include <logdepthbuf_pars_vertex>
        attribute float alpha;
        varying float vAlpha;
        void main() {
          vAlpha = alpha;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          #include <logdepthbuf_vertex>
        }`,
      fragmentShader: /* glsl */ `
        #include <logdepthbuf_pars_fragment>
        uniform float opacity;
        varying float vAlpha;
        void main() {
          #include <logdepthbuf_fragment>
          gl_FragColor = vec4(vec3(0.75, 0.88, 1.0) * vAlpha * opacity, 1.0);
        }`,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
    });
    this.object = new THREE.LineSegments(geo, mat);
    this.object.frustumCulled = false;
    this.object.renderOrder = 4;
  }

  /** velocity: ship velocity in the local (dragged) frame, m/s */
  update(dt: number, velocity: THREE.Vector3, daylight: number): void {
    const speed = velocity.length();
    // Scroll the dust field opposite to motion; very high speeds just scramble it.
    const travel = Math.min(speed * dt, BOX * 0.37);
    if (speed > 0.001) this.offset.addScaledVector(velocity, travel / speed);
    const streak = Math.min(speed * 0.05, 380);
    const dir = speed > 0.001 ? velocity.clone().divideScalar(speed) : new THREE.Vector3();
    const half = BOX / 2;
    for (let i = 0; i < COUNT; i++) {
      const x = mod(this.base[i * 3] - this.offset.x, BOX) - half;
      const y = mod(this.base[i * 3 + 1] - this.offset.y, BOX) - half;
      const z = mod(this.base[i * 3 + 2] - this.offset.z, BOX) - half;
      const j = i * 6;
      this.positions[j] = x;
      this.positions[j + 1] = y;
      this.positions[j + 2] = z;
      this.positions[j + 3] = x - dir.x * streak;
      this.positions[j + 4] = y - dir.y * streak;
      this.positions[j + 5] = z - dir.z * streak;
      const d = Math.sqrt(x * x + y * y + z * z) / half;
      const a = Math.max(0, 1 - d);
      this.alphas[i * 2] = a;
      this.alphas[i * 2 + 1] = 0;
    }
    this.object.geometry.attributes.position.needsUpdate = true;
    this.object.geometry.attributes.alpha.needsUpdate = true;
    const fade = THREE.MathUtils.smoothstep(speed, 40, 900) * (1 - daylight * 0.7);
    (this.object.material as THREE.ShaderMaterial).uniforms.opacity.value = 0.55 * fade;
  }
}

function mod(a: number, n: number): number {
  return ((a % n) + n) % n;
}
